import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Switch } from '@/components/atoms/switch';
import { Label } from '@/components/atoms/label';
import {
  useClientPortalSettings,
  useUpdateClientPortalSettings,
} from '@/hooks/settings/useClientPortalSettings';
import { useHasPermission } from '@/hooks/usePermissions';

export const Route = createFileRoute('/_dashboard/dashboard/settings/client-portal')({
  component: ClientPortalSettingsPage,
});

function ClientPortalSettingsPage() {
  const canView = useHasPermission('company:view');
  const canEdit = useHasPermission('company:edit');
  const settings = useClientPortalSettings();
  const update = useUpdateClientPortalSettings();

  const enabled = settings.data?.enabled ?? false;

  // Direct-URL guard: the nav item is hidden without these permissions, but a
  // pasted link shouldn't render a real-looking settings screen the API would
  // 403 anyway. Mirror the backend gate (view OR edit).
  if (!canView && !canEdit) {
    return (
      <div className="mx-auto w-full max-w-2xl p-4 sm:p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have access to these settings.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold">Client portal</h1>
        <p className="text-sm text-muted-foreground">
          A secure area where your customers sign in to view and pay their invoices, review and
          approve estimates, and manage their upcoming visits.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Access</CardTitle>
        </CardHeader>
        <CardContent>
          {settings.isPending ? (
            <div className="flex justify-center py-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : settings.isError ? (
            <p className="text-sm text-muted-foreground">
              We couldn&apos;t load these settings. Please try again.
            </p>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="portal-enabled" className="text-sm font-medium">
                  Enable the client portal
                </Label>
                <p className="text-xs text-muted-foreground">
                  When on, customers with an email on file can register and sign in. Turning it off
                  immediately blocks new sign-ins.
                </p>
              </div>
              <Switch
                id="portal-enabled"
                checked={enabled}
                disabled={!canEdit || update.isPending}
                onCheckedChange={(next) => update.mutate({ enabled: next })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Invite a specific customer from their contact record (Actions → Client Portal).
      </p>
    </div>
  );
}
